import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * PlaceAutocomplete component using the new Google Places API (PlaceAutocompleteElement)
 * This replaces the legacy Autocomplete component that is no longer available for new projects
 */
const PlaceAutocomplete = ({ 
  value, 
  onChange, 
  placeholder, 
  className,
  testId,
  countryRestrictions = ['be', 'fr', 'nl', 'de', 'lu']
}) => {
  const containerRef = useRef(null);
  const autocompleteElementRef = useRef(null);
  const inputRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const [isLoaded, setIsLoaded] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');

  // Keep onChange ref updated without triggering useEffect
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    let mounted = true;
    let element = null;

    const initAutocomplete = async () => {
      // Wait for Google Maps to be loaded
      if (!window.google || !window.google.maps) {
        // Retry after a short delay
        setTimeout(initAutocomplete, 500);
        return;
      }

      try {
        // Import the places library
        const { PlaceAutocompleteElement } = await window.google.maps.importLibrary('places');
        
        if (!mounted || !containerRef.current) return;

        // Create the autocomplete element
        element = new PlaceAutocompleteElement({
          includedRegionCodes: countryRestrictions,
        });
        
        autocompleteElementRef.current = element;

        // Style the element to match the form
        element.style.width = '100%';
        element.style.fontSize = '16px';

        // Add selection handler
        element.addEventListener('gmp-select', async (event) => {
          try {
            // The event contains placePrediction directly or via detail
            const placePrediction = event.placePrediction || event.detail?.placePrediction;
            console.log('gmp-select event:', event);
            console.log('placePrediction:', placePrediction);
            
            if (placePrediction) {
              const place = placePrediction.toPlace();
              await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location'] });
              
              const address = place.formattedAddress || place.displayName || '';
              console.log('Selected address:', address);
              setInputValue(address);
              if (onChangeRef.current) {
                onChangeRef.current(address);
              }
            } else {
              // Try alternative access pattern
              const place = event.place || event.detail?.place;
              if (place) {
                const address = place.formattedAddress || place.displayName || '';
                console.log('Selected address (alt):', address);
                setInputValue(address);
                if (onChangeRef.current) {
                  onChangeRef.current(address);
                }
              }
            }
          } catch (error) {
            console.error('Error fetching place details:', error);
          }
        });

        // Add error handler
        element.addEventListener('gmp-error', (event) => {
          console.error('PlaceAutocomplete error:', event);
        });

        // Clear the container and append the element
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
          containerRef.current.appendChild(element);
          setIsLoaded(true);
        }
      } catch (error) {
        console.error('Error initializing PlaceAutocompleteElement:', error);
        // Fallback to regular input if the new API fails
        setIsLoaded(false);
      }
    };

    initAutocomplete();

    return () => {
      mounted = false;
      if (element && element.parentNode) {
        element.parentNode.removeChild(element);
      }
    };
  }, [countryRestrictions]);

  // Fallback input handler for manual typing
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  return (
    <div className="place-autocomplete-wrapper">
      {/* Container for the PlaceAutocompleteElement */}
      <div 
        ref={containerRef} 
        className={`place-autocomplete-container ${isLoaded ? 'loaded' : 'hidden'}`}
        data-testid={testId}
      />
      
      {/* Show selected address below the input for visual confirmation */}
      {isLoaded && inputValue && (
        <div className="selected-address-indicator">
          ✓ {inputValue}
        </div>
      )}
      
      {/* Fallback input shown while loading or if API fails */}
      {!isLoaded && (
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          className={className}
          data-testid={testId}
        />
      )}
      
      <style>{`
        .place-autocomplete-wrapper {
          width: 100%;
        }
        .place-autocomplete-container {
          width: 100%;
        }
        .place-autocomplete-container.hidden {
          display: none;
        }
        .place-autocomplete-container.loaded {
          display: block;
        }
        .selected-address-indicator {
          margin-top: 4px;
          padding: 6px 12px;
          background-color: rgba(234, 179, 8, 0.15);
          border: 1px solid rgba(234, 179, 8, 0.4);
          border-radius: 6px;
          font-size: 13px;
          color: #fbbf24;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        /* Style the Google PlaceAutocompleteElement */
        .place-autocomplete-container gmp-place-autocomplete {
          width: 100%;
        }
        .place-autocomplete-container gmp-place-autocomplete::part(input) {
          width: 100%;
          padding: 16px;
          border-radius: 8px;
          border: 2px solid #d1d5db;
          font-size: 16px;
          font-weight: 500;
          color: black;
          background-color: white;
          outline: none;
          box-sizing: border-box;
        }
        .place-autocomplete-container gmp-place-autocomplete::part(input):focus {
          border-color: #eab308;
        }
        .place-autocomplete-container gmp-place-autocomplete::part(input)::placeholder {
          color: #6b7280;
        }
      `}</style>
    </div>
  );
};

export default PlaceAutocomplete;
